// ProductAdmin.jsx
import React, { useEffect, useState, memo, useCallback } from "react";
import axios from "axios";
import { getToken } from "../../utils/auth";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import CONFIG from "../../config";

const ProductAdmin = memo(() => {
  const { t, i18n } = useTranslation();
  // const { formatPrice } = useCurrency();
  const [products, setProducts] = useState([]);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [per] = useState(12);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [sort, setSort] = useState("id_desc");
  const navigate = useNavigate();

  const isArabic = i18n.language?.startsWith('ar');

  const fetchProducts = useCallback(async (p = page) => {
    setLoading(true);
    try {
      const token = getToken();
      const headers = {};
      if (token) headers.Authorization = `Bearer ${token}`;

      const res = await axios.get(`${CONFIG.API_URL}/api/products`, {
        params: { q, page: p, per, sort },
        headers
      });
      setProducts(res.data.data || []);
      setTotal(res.data.meta?.total || res.data.total || 0);
      setPage(p);
    } catch (err) {
      console.error(err);
      alert("Failed to fetch products");
    } finally { setLoading(false); }
  }, [q, page, per, sort]);

  useEffect(() => { fetchProducts(1); }, [fetchProducts]);
  useEffect(() => { if (page !== 1) fetchProducts(page); }, [page, fetchProducts]);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try {
      const token = getToken();
      const headers = {};
      if (token) headers.Authorization = `Bearer ${token}`;
      await axios.delete(`${CONFIG.API_URL}/api/products/${id}`, { headers });
      fetchProducts();
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.message || "Delete failed");
    }
  };

  const handleResetStats = async (id) => {
    if (!window.confirm("Reset likes & views?")) return;
    try {
      const token = getToken();
      const headers = {};
      if (token) headers.Authorization = `Bearer ${token}`;
      await axios.post(`${CONFIG.API_URL}/api/products/${id}/reset-stats`, {}, { headers });
      fetchProducts();
    } catch (err) {
      console.error(err);
      alert("Reset failed");
    }
  };

  return (
    <div className="max-w-7xl mx-auto my-12 p-8 bg-white rounded-[40px] shadow-2xl border border-gray-100 mt-24">
      <div className="flex justify-between items-center mb-12">
        <h2 className="text-4xl font-black text-gray-900 tracking-tight">{t('admin.products_page.title')}</h2>
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate("/admin")}
            className="flex items-center gap-2 text-gray-500 hover:text-brand-primary transition-all text-sm font-medium group"
          >
            <span className={`transition-transform group-hover:-translate-x-1 ${i18n.language?.startsWith('ar') ? "rotate-180 group-hover:translate-x-1" : ""}`}>←</span>
            {t('admin.common.back', 'Back')}
          </button>
          <div className="flex gap-2">
            <button
              onClick={() => navigate("/admin/create-product")}
              className="bg-brand-primary hover:bg-brand-primary-hover text-white px-6 py-3 rounded-2xl font-black text-xs uppercase tracking-widest shadow-lg shadow-brand-primary/20 hover:scale-105 active:scale-95 transition-all"
            >
              {t('admin.products_page.create')}
            </button>
          </div>
        </div>
      </div>

      <div className="mb-8 flex flex-col md:flex-row gap-4 items-center bg-gray-50 p-4 rounded-[20px] border border-gray-100">
        <div className="relative flex-1 w-full">
          <i className="fas fa-search absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"></i>
          <input value={q} onChange={e => setQ(e.target.value)} placeholder={t('admin.common.search_placeholder', 'Search by name, ID...')} className="w-full pl-12 pr-4 py-3 rounded-xl border-none bg-white shadow-sm focus:ring-2 focus:ring-brand-primary/20 transition-all font-medium" />
        </div>
        <select value={sort} onChange={e => setSort(e.target.value)} className="p-3 rounded-xl border-none bg-white shadow-sm focus:ring-2 focus:ring-brand-primary/20 font-medium text-sm w-full md:w-auto">
          <option value="id_desc">{t('admin.products_page.sort.id_desc')}</option>
          <option value="id_asc">{t('admin.products_page.sort.id_asc')}</option>
          <option value="newest">{t('admin.products_page.sort.newest')}</option>
          <option value="popular">{t('admin.products_page.sort.popular')}</option>
          <option value="price_asc">{t('admin.products_page.sort.price_asc')}</option>
          <option value="price_desc">{t('admin.products_page.sort.price_desc')}</option>
        </select>
      </div>

      {
        loading ? <div className="p-12 text-center text-gray-500 font-medium animate-pulse">{t('admin.common.loading')}</div> : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {products.map(p => (
                <div key={p.id} className="group bg-white rounded-3xl border border-gray-100 overflow-hidden hover:shadow-xl hover:shadow-brand-primary/5 hover:border-brand-primary/20 transition-all duration-300 flex flex-col relative">
                  <div className="absolute top-4 left-4 z-10">
                    <span className="px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest backdrop-blur-md bg-black/50 text-white">
                      #{p.id}
                    </span>
                  </div>
                  <div className="h-48 overflow-hidden relative">
                    <img src={p.imageUrl || (p.thumbnailUrls && p.thumbnailUrls[0]) || "https://via.placeholder.com/400x300"} alt={p.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                    <div className="absolute top-2 right-2">
                      <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest backdrop-blur-md shadow-sm ${p.is_featured ? "bg-white/90 text-amber-600" : "bg-white/90 text-gray-400"}`}>
                        {p.is_featured ? t('admin.products_page.featured', 'Featured') : t('admin.products_page.regular', 'Regular')}
                      </span>
                    </div>
                  </div>
                  <div className="p-5 flex-1 flex flex-col">
                    <h3 className="font-bold text-gray-900 mb-1 line-clamp-1">{i18n.language?.startsWith('ar') && p.name_ar ? p.name_ar : p.name}</h3>
                    <p className="text-xs font-medium text-gray-500 line-clamp-2 mb-4">{i18n.language?.startsWith('ar') && p.description_ar ? p.description_ar : p.description}</p>

                    <div className="mt-auto flex items-center justify-between border-t border-gray-50 pt-4">
                      <div className="flex gap-3 text-xs font-bold text-gray-400">
                        <span className="flex items-center gap-1" title={t('admin.products_page.views_title', 'Views Count')}><i className="fas fa-eye"></i> {p.views || 0}</span>
                        <span className="flex items-center gap-1" title={t('admin.products_page.likes_title', 'Likes Count')}><i className="fas fa-heart"></i> {p.likes || 0}</span>
                      </div>
                      <div className="flex gap-2">
                        <button onClick={async () => {
                          try {
                            const res = await axios.post(`${CONFIG.API_URL}/api/products/product/${p.id}/toggle-featured`, {}, { headers: { Authorization: `Bearer ${getToken()}` } });
                            setProducts(prev => prev.map(item => item.id === p.id ? { ...item, is_featured: res.data.is_featured } : item));
                          } catch (err) { alert("Toggle failed"); }
                        }} className="w-8 h-8 rounded-full bg-amber-50 text-amber-500 hover:bg-amber-500 hover:text-white flex items-center justify-center transition-all" title={t('admin.products_page.actions.toggle_featured', 'Toggle Featured')}>
                          <i className="fas fa-star text-xs"></i>
                        </button>
                        <button onClick={() => navigate(`/admin/edit-product/${p.id}`)} className="w-8 h-8 rounded-full bg-blue-50 text-blue-500 hover:bg-blue-500 hover:text-white flex items-center justify-center transition-all" title={t('admin.products_page.actions.edit', 'Edit')}>
                          <i className="fas fa-edit text-xs"></i>
                        </button>
                        <button onClick={() => handleResetStats(p.id)} className="w-8 h-8 rounded-full bg-indigo-50 text-indigo-500 hover:bg-indigo-500 hover:text-white flex items-center justify-center transition-all" title={t('admin.products_page.actions.reset_stats', 'Reset Stats')}>
                          <i className="fas fa-sync-alt text-xs"></i>
                        </button>
                        <button onClick={() => handleDelete(p.id)} className="w-8 h-8 rounded-full bg-red-50 text-red-500 hover:bg-red-500 hover:text-white flex items-center justify-center transition-all" title={t('admin.products_page.actions.delete', 'Delete')}>
                          <i className="fas fa-trash text-xs"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {total > per && (
              <div className="flex justify-center gap-4 items-center mt-12">
                <button onClick={() => setPage(s => Math.max(1, s - 1))} disabled={page <= 1} className="w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center disabled:opacity-50 transition-all font-bold">{isArabic ? "→" : "←"}</button>
                <div className="text-xs font-black uppercase tracking-widest text-gray-400">{t('admin.common.page', { current: page, total: Math.ceil(total / per) || 1 })}</div>
                <button onClick={() => setPage(s => s + 1)} disabled={page >= Math.ceil(total / per)} className="w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center disabled:opacity-50 transition-all font-bold">{isArabic ? "←" : "→"}</button>
              </div>
            )}
          </>
        )
      }
    </div >
  );
});

export default ProductAdmin;
